import { useState, useMemo } from 'react'
import { Download, FileText, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { jsPDF } from 'jspdf'

import { loadLogoForPdf, C, setRgb, fillRgb, drawRgb, wrapText, PW, PH, ML, MR, MT, MB, CW, addPageChrome, RenderCtx, ensureSpace, renderH2, renderH3, renderParagraph, renderBullet, renderNumbered, renderKpiTable, renderMarketFigures, renderRiskRow, renderVerdictBlock, renderLabelValue } from '@/lib/pdf-utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import type {
  StructuredMemo,
  MemoSection,
  MemoMarketSection,
  MemoProductSection,
  MemoRiskSection,
  MemoRecommendation,
} from '@/types'

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface InvestmentMemoSectionProps {
  memo: StructuredMemo | string
  companyName: string
  analysisDate?: string
  recommendation?: string
  scores?: {
    team?: number
    market?: number
    product?: number
    competition?: number
    overall?: number
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Type guard
// ─────────────────────────────────────────────────────────────────────────────

function isStructuredMemo(memo: StructuredMemo | string): memo is StructuredMemo {
  return typeof memo === 'object' && memo !== null && 'company_overview' in memo
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF helpers
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Structured PDF renderer (new schema)
// ─────────────────────────────────────────────────────────────────────────────

async function generateStructuredPdf(
  memo: StructuredMemo,
  companyName: string,
  analysisDate: string,
  recommendation: string | undefined,
  scores: InvestmentMemoSectionProps['scores'],
): Promise<jsPDF> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  let logoData: { data: string; width: number; height: number } | null = null
  try { logoData = await loadLogoForPdf() } catch { /* no logo */ }

  // ── Cover page ───────────────────────────────────────────────────────────
  // Dark background
  fillRgb(doc, C.navy)
  doc.rect(0, 0, PW, PH, 'F')

  // Top gradient bar (3-stop: blue → pink)
  fillRgb(doc, C.blue)
  doc.rect(0, 0, PW * 0.5, 4, 'F')
  fillRgb(doc, [147, 88, 233])  // purple midpoint
  doc.rect(PW * 0.5, 0, PW * 0.25, 4, 'F')
  fillRgb(doc, C.pink)
  doc.rect(PW * 0.75, 0, PW * 0.25, 4, 'F')

  // Logo top-left
  if (logoData) {
    const lh = 10
    const lw = (logoData.width / logoData.height) * lh
    doc.addImage(logoData.data, 'PNG', 18, 14, lw, lh)
  }
  doc.setFontSize(7)
  doc.setFont('helvetica', 'bold')
  setRgb(doc, [180, 190, 210])
  doc.text('uniscout.ai', 30, 22)

  // CONFIDENTIAL badge top-right
  fillRgb(doc, [30, 41, 59])
  doc.roundedRect(PW - 50, 13, 32, 8, 2, 2, 'F')
  doc.setFontSize(7)
  doc.setFont('helvetica', 'bold')
  setRgb(doc, C.pink)
  doc.text('CONFIDENTIAL', PW - 34, 18.5, { align: 'center' })

  // Company name
  doc.setFontSize(30)
  doc.setFont('helvetica', 'bold')
  setRgb(doc, C.white)
  const nameLines = wrapText(doc, companyName, PW - 36)
  let ny = 70
  for (const l of nameLines) { doc.text(l, 18, ny); ny += 12 }

  // Subtitle
  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  setRgb(doc, [148, 163, 184])
  doc.text('Investment Memorandum', 18, ny + 2)
  doc.setFontSize(9)
  setRgb(doc, [100, 116, 139])
  doc.text(`Prepared: ${analysisDate}`, 18, ny + 9)

  // Recommendation badge
  if (recommendation) {
    const recColor: [number, number, number] =
      recommendation.toLowerCase().includes('invest') ? C.emerald :
      recommendation.toLowerCase().includes('hold')  ? C.amber :
      C.rose
    fillRgb(doc, recColor)
    doc.roundedRect(18, ny + 16, 60, 10, 2.5, 2.5, 'F')
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    setRgb(doc, C.white)
    doc.text(recommendation, 48, ny + 23, { align: 'center' })
  }

  // Score cards grid (4 across)
  if (scores) {
    const cardY = 175
    const cards = [
      { label: 'TEAM',        value: scores.team },
      { label: 'MARKET',      value: scores.market },
      { label: 'PRODUCT',     value: scores.product },
      { label: 'COMPETITION', value: scores.competition },
    ]
    const cw = (PW - 36 - 9) / 4
    cards.forEach((card, i) => {
      const cx = 18 + i * (cw + 3)
      fillRgb(doc, C.navyMid)
      doc.roundedRect(cx, cardY, cw, 22, 2, 2, 'F')
      doc.setFontSize(6.5)
      doc.setFont('helvetica', 'bold')
      setRgb(doc, [148, 163, 184])
      doc.text(card.label, cx + cw / 2, cardY + 6.5, { align: 'center' })
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      setRgb(doc, C.white)
      doc.text(card.value != null ? card.value.toFixed(1) : '—', cx + cw / 2, cardY + 17, { align: 'center' })
    })

    // Overall score
    if (scores.overall != null) {
      const overall = scores.overall
      const overallColor: [number, number, number] =
        overall >= 7.5 ? C.emerald : overall >= 5.5 ? C.amber : C.rose
      fillRgb(doc, overallColor)
      doc.roundedRect(18, cardY + 27, PW - 36, 18, 2, 2, 'F')
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      setRgb(doc, C.white)
      doc.text('OVERALL INVESTABILITY SCORE', PW / 2, cardY + 34, { align: 'center' })
      doc.setFontSize(18)
      doc.text(`${overall.toFixed(1)} / 10`, PW / 2, cardY + 42, { align: 'center' })
    }
  }

  // Bottom disclaimer on cover
  doc.setFontSize(6.5)
  doc.setFont('helvetica', 'normal')
  setRgb(doc, [71, 85, 105])
  const disclaimer = 'This document is confidential and intended solely for the addressee. AI-generated analysis — verify independently before making investment decisions.'
  const dlines = wrapText(doc, disclaimer, PW - 36)
  let dy = PH - 18
  for (const dl of dlines) { doc.text(dl, PW / 2, dy, { align: 'center' }); dy += 4 }

  // ── Count content pages (needed for footer page N/M) ─────────────────────
  // Rough estimate: each paragraph ~1 page / 4, plus section headers
  // We'll do two-pass — first render to count pages, then re-render.
  // For simplicity here we use a single-pass with totalPages = estimated.
  // jsPDF doesn't expose final page count until done, so we use a known-max
  // and overwrite footer in a post-process. This is a known jsPDF limitation.
  // We set a placeholder totalPages value; the actual approach is single-pass.
  const TOTAL_PAGES_PLACEHOLDER = 99

  // ── Content pages ─────────────────────────────────────────────────────────
  doc.addPage()
  let pageNum = 2
  addPageChrome(doc, companyName, analysisDate, pageNum, TOTAL_PAGES_PLACEHOLDER, logoData)

  const ctx: RenderCtx = {
    doc,
    y: MT + 14,
    companyName,
    date: analysisDate,
    pageNum,
    totalPages: TOTAL_PAGES_PLACEHOLDER,
    logoData,
  }

  // Helper: render a MemoSection (prose paragraphs)
  const renderSection = (section: MemoSection | undefined) => {
    if (!section?.paragraphs) return
    for (const para of section.paragraphs) {
      renderParagraph(ctx, para)
    }
    ctx.y += 2
  }

  // 1. Company Overview
  renderH2(ctx, '1. Company Overview')
  renderSection(memo.company_overview)

  // 2. Team & Leadership
  renderH2(ctx, '2. Team & Leadership')
  renderSection(memo.team_leadership)

  // 3. Market Opportunity
  renderH2(ctx, '3. Market Opportunity')
  renderSection(memo.market_opportunity)
  if (memo.market_opportunity) {
    renderMarketFigures(
      ctx,
      memo.market_opportunity.tam,
      memo.market_opportunity.sam,
      memo.market_opportunity.som,
      memo.market_opportunity.cagr,
    )
  }

  // 4. Product & Traction
  renderH2(ctx, '4. Product & Traction')
  renderSection(memo.product_traction)
  if (memo.product_traction?.kpi_metrics?.length) {
    renderH3(ctx, 'Key Metrics')
    renderKpiTable(ctx, memo.product_traction.kpi_metrics)
    ctx.y += 4
  }

  // 5. Competitive Landscape
  renderH2(ctx, '5. Competitive Landscape')
  renderSection(memo.competitive_landscape)

  // 6. Risk Factors
  renderH2(ctx, '6. Risk Factors')
  const risks = memo.risk_factors
  if (risks) {
    renderParagraph(ctx, risks.overview)
    if (risks.risks?.length) {
      renderH3(ctx, 'Key Risk Factors')
      ctx.y += 2
      for (const r of risks.risks) {
        renderRiskRow(ctx, r.name, r.description, r.severity)
      }
      ctx.y += 2
    }
    renderH3(ctx, 'Mitigation Strategies')
    renderParagraph(ctx, risks.mitigation)
  }

  // 7. Investment Recommendation
  renderH2(ctx, '7. Investment Recommendation')
  const rec: MemoRecommendation | undefined = memo.recommendation
  if (rec) {
    renderVerdictBlock(ctx, rec.verdict, rec.rationale)
    ctx.y += 4

    // Terms
    renderH3(ctx, 'Investment Terms')
    renderLabelValue(ctx, 'Suggested Valuation', rec.terms.suggested_valuation)
    renderLabelValue(ctx, 'Investment Size',     rec.terms.investment_size)
    renderLabelValue(ctx, 'Structure',           rec.terms.structure)
    if (rec.terms.key_terms?.length) {
      ctx.y += 2
      renderH3(ctx, 'Key Terms')
      for (const t of rec.terms.key_terms) renderBullet(ctx, t)
    }
    ctx.y += 4

    // Conditions
    if (rec.conditions?.length) {
      renderH3(ctx, 'Investment Conditions')
      for (const c of rec.conditions) renderBullet(ctx, c)
      ctx.y += 4
    }

    // Next steps
    if (rec.next_steps?.length) {
      renderH3(ctx, 'Next Steps')
      rec.next_steps.forEach((s, i) => renderNumbered(ctx, i + 1, s))
      ctx.y += 4
    }

    // Due diligence
    if (rec.due_diligence?.length) {
      renderH3(ctx, 'Outstanding Due Diligence')
      for (const d of rec.due_diligence) renderBullet(ctx, d)
    }
  }

  // ── Fix page numbers (replace placeholder) ───────────────────────────────
  const finalPageCount = doc.getNumberOfPages()
  for (let p = 2; p <= finalPageCount; p++) {
    doc.setPage(p)
    // Overwrite just the page counter in the footer
    fillRgb(doc, C.offWhite)
    doc.rect(PW - MR - 20, PH - 10, 22, 8, 'F')
    doc.setFontSize(6.5)
    doc.setFont('helvetica', 'normal')
    setRgb(doc, C.slate500)
    doc.text(`${p} / ${finalPageCount}`, PW - MR, PH - 5.5, { align: 'right' })
  }

  return doc
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy string PDF renderer (fallback for old analyses)
// ─────────────────────────────────────────────────────────────────────────────

async function generateLegacyPdf(
  memoStr: string,
  companyName: string,
  analysisDate: string,
  recommendation: string | undefined,
): Promise<jsPDF> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  let logoData: { data: string; width: number; height: number } | null = null
  try { logoData = await loadLogoForPdf() } catch { /* no logo */ }

  addPageChrome(doc, companyName, analysisDate, 1, 1, logoData)

  const ctx: RenderCtx = {
    doc, y: MT + 14, companyName, date: analysisDate,
    pageNum: 1, totalPages: 1, logoData,
  }

  // Strip markdown and render lines as prose paragraphs
  const lines = memoStr.split('\n')
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) { ctx.y += 2; continue }
    if (line.startsWith('## ')) { renderH2(ctx, line.replace(/^## /, '')); continue }
    if (line.startsWith('### ')) { renderH3(ctx, line.replace(/^### /, '')); continue }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      renderBullet(ctx, line.replace(/^[-*] /, '')); continue
    }
    if (/^\d+\. /.test(line)) {
      const m = line.match(/^(\d+)\. (.*)/)
      if (m) renderNumbered(ctx, parseInt(m[1]), m[2])
      continue
    }
    renderParagraph(ctx, line.replace(/\*\*(.+?)\*\*/g, '$1'))
  }

  const finalPageCount = doc.getNumberOfPages()
  for (let p = 1; p <= finalPageCount; p++) {
    doc.setPage(p)
    fillRgb(doc, C.offWhite)
    doc.rect(PW - MR - 20, PH - 10, 22, 8, 'F')
    doc.setFontSize(6.5)
    doc.setFont('helvetica', 'normal')
    setRgb(doc, C.slate500)
    doc.text(`${p} / ${finalPageCount}`, PW - MR, PH - 5.5, { align: 'right' })
  }

  return doc
}

// ─────────────────────────────────────────────────────────────────────────────
// Web view — Structured Memo renderer
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_TITLES: Array<{ key: keyof StructuredMemo; title: string }> = [
  { key: 'company_overview',    title: 'Company Overview' },
  { key: 'team_leadership',     title: 'Team & Leadership' },
  { key: 'market_opportunity',  title: 'Market Opportunity' },
  { key: 'product_traction',    title: 'Product & Traction' },
  { key: 'competitive_landscape', title: 'Competitive Landscape' },
  { key: 'risk_factors',        title: 'Risk Factors' },
  { key: 'recommendation',      title: 'Investment Recommendation' },
]

const SEV_BADGE: Record<string, string> = {
  low:    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  high:   'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
}

function ProseParagraphs({ paragraphs }: { paragraphs: string[] }) {
  return (
    <>
      {paragraphs.map((p, i) => (
        <p
          key={i}
          className="text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-5 text-justify"
        >
          {p}
        </p>
      ))}
    </>
  )
}

function SectionHeading({ n, title }: { n: number; title: string }) {
  return (
    <h2 className="flex items-center gap-3 text-xl font-black text-slate-900 dark:text-white mt-10 mb-4 pb-2 border-b border-slate-100 dark:border-slate-800">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-[#4194F2] to-[#E91F84] flex items-center justify-center text-white text-xs font-bold">
        {n}
      </span>
      {title}
    </h2>
  )
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-base font-bold text-[#4194F2] mt-6 mb-3">
      <span className="w-1.5 h-1.5 rounded-full bg-[#4194F2] flex-shrink-0" />
      {children}
    </h3>
  )
}

function MarketFigures({ section }: { section: MemoMarketSection }) {
  const items = [
    { label: 'TAM', value: section.tam, cls: 'bg-blue-600' },
    { label: 'SAM', value: section.sam, cls: 'bg-blue-500' },
    { label: 'SOM', value: section.som, cls: 'bg-pink-500' },
  ]
  return (
    <div className="grid grid-cols-3 gap-3 my-5">
      {items.map(({ label, value, cls }) => (
        <div key={label} className={`${cls} rounded-xl p-4 text-white`}>
          <div className="text-xs font-bold opacity-70 mb-1">{label}</div>
          <div className="text-base font-bold leading-tight">{value}</div>
        </div>
      ))}
      {section.cagr && (
        <div className="col-span-3 text-xs text-slate-500 dark:text-slate-400 font-semibold">
          Market CAGR: {section.cagr}
        </div>
      )}
    </div>
  )
}

function KpiGrid({ kpis }: { kpis: { label: string; value: string }[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-5">
      {kpis.map(({ label, value }) => (
        <div key={label} className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
            {label}
          </div>
          <div className="text-base font-bold text-slate-900 dark:text-white">{value}</div>
        </div>
      ))}
    </div>
  )
}

function RiskTable({ risks }: { risks: { name: string; description: string; severity: string }[] }) {
  return (
    <div className="space-y-2 my-4">
      {risks.map((r, i) => (
        <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <div className="flex-shrink-0 w-1.5 self-stretch rounded-full bg-current" style={{
            color: r.severity === 'high' ? '#f43f5e' : r.severity === 'medium' ? '#f59e0b' : '#10b981'
          }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-bold text-slate-900 dark:text-white">{r.name}</span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${SEV_BADGE[r.severity] ?? SEV_BADGE.medium}`}>
                {r.severity}
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">{r.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

function RecommendationBlock({ rec }: { rec: MemoRecommendation }) {
  const verdictColor =
    rec.verdict === 'Strong Invest' || rec.verdict === 'Invest'
      ? 'from-emerald-500 to-emerald-600'
      : rec.verdict === 'Hold'
      ? 'from-amber-500 to-amber-600'
      : 'from-rose-500 to-rose-600'

  return (
    <div className="mt-4 space-y-5">
      {/* Verdict banner */}
      <div className={`bg-gradient-to-r ${verdictColor} rounded-xl p-5 text-white`}>
        <div className="text-xs font-bold opacity-70 uppercase tracking-widest mb-1">Recommendation</div>
        <div className="text-2xl font-black mb-2">{rec.verdict}</div>
        <p className="text-sm opacity-90 leading-relaxed">{rec.rationale}</p>
      </div>

      {/* Terms */}
      <div>
        <SubHeading>Investment Terms</SubHeading>
        <div className="space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
          <div><span className="font-semibold text-slate-800 dark:text-slate-200">Valuation: </span>{rec.terms.suggested_valuation}</div>
          <div><span className="font-semibold text-slate-800 dark:text-slate-200">Investment Size: </span>{rec.terms.investment_size}</div>
          <div><span className="font-semibold text-slate-800 dark:text-slate-200">Structure: </span>{rec.terms.structure}</div>
        </div>
        {rec.terms.key_terms?.length > 0 && (
          <ul className="mt-3 space-y-1.5 pl-4">
            {rec.terms.key_terms.map((t, i) => (
              <li key={i} className="text-sm text-slate-600 dark:text-slate-300 flex gap-2">
                <span className="text-[#4194F2] flex-shrink-0">—</span>{t}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Conditions */}
      {rec.conditions?.length > 0 && (
        <div>
          <SubHeading>Investment Conditions</SubHeading>
          <ul className="space-y-1.5 pl-4">
            {rec.conditions.map((c, i) => (
              <li key={i} className="text-sm text-slate-600 dark:text-slate-300 flex gap-2">
                <span className="text-[#4194F2] flex-shrink-0">—</span>{c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next steps */}
      {rec.next_steps?.length > 0 && (
        <div>
          <SubHeading>Next Steps</SubHeading>
          <ol className="space-y-1.5 pl-4 list-decimal">
            {rec.next_steps.map((s, i) => (
              <li key={i} className="text-sm text-slate-600 dark:text-slate-300">{s}</li>
            ))}
          </ol>
        </div>
      )}

      {/* DD */}
      {rec.due_diligence?.length > 0 && (
        <div>
          <SubHeading>Outstanding Due Diligence</SubHeading>
          <ul className="space-y-1.5 pl-4">
            {rec.due_diligence.map((d, i) => (
              <li key={i} className="text-sm text-slate-600 dark:text-slate-300 flex gap-2">
                <span className="text-amber-500 flex-shrink-0">◦</span>{d}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function StructuredMemoWebView({ memo }: { memo: StructuredMemo }) {
  return (
    <article className="font-['Inter',_system-ui,_sans-serif]">
      {SECTION_TITLES.map(({ key, title }, idx) => {
        const section = memo[key]
        if (!section) return null
        return (
          <section key={key}>
            <SectionHeading n={idx + 1} title={title} />
            {key === 'market_opportunity' && (() => {
              const s = section as MemoMarketSection
              return (
                <>
                  <ProseParagraphs paragraphs={s.paragraphs} />
                  <MarketFigures section={s} />
                </>
              )
            })()}
            {key === 'product_traction' && (() => {
              const s = section as MemoProductSection
              return (
                <>
                  <ProseParagraphs paragraphs={s.paragraphs} />
                  {s.kpi_metrics?.length > 0 && (
                    <>
                      <SubHeading>Key Metrics</SubHeading>
                      <KpiGrid kpis={s.kpi_metrics} />
                    </>
                  )}
                </>
              )
            })()}
            {key === 'risk_factors' && (() => {
              const s = section as MemoRiskSection
              return (
                <>
                  <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-4 text-justify">
                    {s.overview}
                  </p>
                  {s.risks?.length > 0 && (
                    <>
                      <SubHeading>Key Risk Factors</SubHeading>
                      <RiskTable risks={s.risks} />
                    </>
                  )}
                  <SubHeading>Mitigation Strategies</SubHeading>
                  <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed text-justify">
                    {s.mitigation}
                  </p>
                </>
              )
            })()}
            {key === 'recommendation' && (
              <RecommendationBlock rec={section as MemoRecommendation} />
            )}
            {key !== 'market_opportunity' &&
              key !== 'product_traction' &&
              key !== 'risk_factors' &&
              key !== 'recommendation' && (
              <ProseParagraphs paragraphs={(section as MemoSection).paragraphs} />
            )}
          </section>
        )
      })}
    </article>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Style tokens (web view)
// ─────────────────────────────────────────────────────────────────────────────
const headingFont = { fontFamily: '"Outfit", "Inter", sans-serif' }

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function InvestmentMemoSection({
  memo,
  companyName,
  analysisDate,
  recommendation,
  scores,
}: InvestmentMemoSectionProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const { toast } = useToast()

  const actualMemo = useMemo(() => {
    if (typeof memo === 'string') {
      try {
        const parsed = JSON.parse(memo)
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed
        }
      } catch (e) {
        // Not JSON, ignore
      }
    }
    return memo
  }, [memo])

  const structured = isStructuredMemo(actualMemo)
  const date = analysisDate || new Date().toLocaleDateString()

  const handleDownloadPdf = async () => {
    setIsDownloading(true)
    try {
      let doc: jsPDF
      if (structured) {
        doc = await generateStructuredPdf(actualMemo as StructuredMemo, companyName, date, recommendation, scores)
      } else {
        doc = await generateLegacyPdf(actualMemo as string, companyName, date, recommendation)
      }
      const filename = `${companyName.replace(/[^a-zA-Z0-9]/g, '_')}_Investment_Memo.pdf`
      doc.save(filename)
      toast({ title: 'PDF downloaded', description: filename })
    } catch (err) {
      console.error('PDF generation error:', err)
      toast({ title: 'Download failed', description: String(err), variant: 'destructive' })
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Card className="border-0 shadow-none bg-transparent">
      <CardContent className="p-0">
        {/* Document on a desk effect */}
        <div className="bg-slate-200/50 dark:bg-slate-900/50 p-4 sm:p-8 rounded-xl">
          <div className="max-w-4xl mx-auto bg-white dark:bg-[#0F172A] shadow-2xl rounded-sm overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col" style={{ height: '800px' }}>

            {/* Sticky header */}
            <div className="sticky top-0 z-10 flex-shrink-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-8 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h4
                  className="text-2xl font-black text-slate-900 dark:text-white"
                  style={headingFont}
                >
                  Investment Memorandum
                </h4>
                <p className="text-lg font-bold text-slate-600 dark:text-slate-300 mt-0.5">
                  {companyName}
                </p>
                <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 dark:text-slate-400 font-semibold">
                  <span>Date: {date}</span>
                  {recommendation && (
                    <>
                      <span className="text-slate-200 dark:text-slate-700">|</span>
                      <span>
                        Recommendation:{' '}
                        <span className="logo-gradient-text">{recommendation}</span>
                      </span>
                    </>
                  )}
                  {!structured && (
                    <>
                      <span className="text-slate-200 dark:text-slate-700">|</span>
                      <span className="text-amber-500 text-xs font-bold uppercase tracking-wider">
                        Legacy format
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="uniscout.ai" className="w-14 h-14 object-contain" />
                <Button
                  onClick={handleDownloadPdf}
                  disabled={isDownloading}
                  size="sm"
                  className="bg-gradient-to-r from-[#4194F2] to-[#E91F84] text-white hover:opacity-90 transition-opacity gap-2"
                >
                  {isDownloading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                  ) : (
                    <><Download className="w-4 h-4" /> Download PDF</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-8 py-8">
            {structured ? (
              <StructuredMemoWebView memo={actualMemo as StructuredMemo} />
            ) : (
              /* Legacy markdown fallback */
              <article className="prose prose-slate dark:prose-invert max-w-none
                prose-headings:font-black prose-headings:tracking-tight
                prose-h2:text-2xl prose-h2:text-slate-800 dark:prose-h2:text-slate-200 prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:text-slate-800 dark:prose-h3:text-slate-200 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-base prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-justify
                prose-strong:text-[#4194F2] dark:prose-strong:text-[#4194F2] prose-strong:font-bold
                prose-ul:my-5 prose-ul:space-y-2 prose-ul:pl-6
                prose-ol:my-5 prose-ol:space-y-2 prose-ol:pl-6
                prose-li:text-base prose-li:text-slate-600 dark:prose-li:text-slate-300
                font-['Inter',_system-ui,_sans-serif]
              ">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {actualMemo as string}
                </ReactMarkdown>
              </article>
            )}
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 flex-shrink-0 bg-slate-50 dark:bg-slate-800/50 px-8 py-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="uniscout.ai" className="w-9 h-9 object-contain" />
                <div>
                  <span
                    className="text-sm font-black logo-gradient-text"
                    style={headingFont}
                  >
                    uniscout.ai
                  </span>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-2 font-bold uppercase tracking-wider">
                    AI-Powered Investment Analysis
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">
                  Confidential — Internal Document
                </p>
              </div>
            </div>
          </div>

          </div>
        </div>
      </CardContent>
    </Card>
  )
}
